/**
 * Gemini Enterprise is the navigation contract for every governed AI surface.
 *
 * Keeping the route maps here keeps every product's sidebar section consistent.
 * Products retain their own data and route namespace; the labels,
 * hierarchy, and ordering are shared.
 */

const item = (base, path, icon, labelKey, label) => ({
  to: `${base}/${path}`,
  icon,
  labelKey,
  label,
});

const geminiGroups = (base, paths = {}) => ({
  overview: [
    item(base, paths.dashboard || 'dashboard', 'grid', 'activationDashboard'),
    item(base, paths.recommendations || 'recommendations', 'zap', 'recommendations'),
    item(base, paths.registry || 'registry', 'list', 'agentRegistry'),
    item(base, paths.users || 'users', 'users', 'users'),
    item(base, paths.agentNetwork || 'agent-network', 'share-2', 'agentNetwork'),
  ],
  adoption: [
    item(base, paths.usageRoi || 'usage-roi', 'trending-up', 'usageAndRoi'),
    item(base, paths.conversations || 'conversations', 'message-circle', 'conversations'),
    item(base, paths.knowledgeGaps || 'knowledge-gaps', 'book-open', 'knowledgeGaps'),
    item(base, paths.intentAnalysis || 'intent-analysis', 'target', 'intentAnalysis'),
    item(base, paths.sources || 'sources', 'database', 'sourceIntelligence'),
  ],
  // Notifications are not a Security page: they sit under Policy in every module.
  // Label key 'alerts' renders "Notifications".
  notifications: item(base, paths.alerts || 'alerts', 'bell', 'alerts'),
  security: [
    item(base, paths.signals || 'security/signals', 'activity', 'signals'),
    item(base, paths.hunt || 'security/hunt', 'search', 'hunt'),
    item(base, paths.incidents || 'security/incidents', 'alert-octagon', 'incidents'),
    item(base, paths.skills || 'security/skills', 'layers', 'skills'),
    item(base, paths.response || 'security/response', 'zap', 'response'),
    item(base, paths.exceptions || 'security/exceptions', 'slash', 'exceptions'),
    item(base, paths.aiBom || 'security/ai-bom', 'package', 'aiBomInventory'),
    item(base, paths.posture || 'posture', 'lock', 'accessManagement'),
  ],
  // Policy is its own item, as in Atlas (Govern: Policy, then the Compliance pages) — not a
  // Compliance sub-page. Only modules with a Policy page (Atlas Policy pinned to them).
  policy: paths.policy ? item(base, paths.policy, 'lock', 'policy') : null,
  // The Compliance group, as in Atlas and every module: the module's Compliance page and its
  // Audit Report (`auditReport: null` for a module that has none yet). Log Explorer is its own
  // link right below the group.
  compliance: item(base, paths.compliance || 'compliance', 'shield', 'compliance'),
  auditReport: paths.auditReport === null ? null : item(base, paths.auditReport || 'audit-report', 'file-text', 'auditReport'),
  logExplorer: item(base, paths.logExplorer || paths.auditLog || 'log-explorer', 'file-text', 'logExplorer'),
  settings: item(base, paths.settings || 'settings', 'settings', 'settings'),
});

export const getCopilotNavGroups = (studio = false) => (
  geminiGroups(studio ? '/copilot/studio' : '/copilot')
);

export const getSaasAiNavGroups = () => geminiGroups('/saas-ai', { policy: 'policy' });

/**
 * Cloud Discovery has no Adoption group: usage, conversations and intent are
 * questions about people using an assistant, and a cloud audit log answers
 * none of them. What was filed there under borrowed names — the workload
 * inventory as "Source Intelligence", knowledge bases as "Knowledge Gaps" — is
 * discovery, so it moves up beside the registry under its own name.
 */
export const getCloudDiscoveryNavGroups = () => {
  const groups = geminiGroups('/cloud', {
    dashboard: 'overview',
    agentNetwork: 'topology',
    registry: 'agents',
    policy: 'policy',
    // The Atlas audit report does not read cloud posture evidence.
    auditReport: null,
  });
  return {
    ...groups,
    overview: [
      // No Agent Network: the cloud topology view drew accounts, services and models as a
      // graph with no agents in it to connect (see /cloud/agents), so it read as a network
      // of nothing. Agent-to-identity links live on the Agents and Identities pages.
      ...groups.overview
        .filter((navItem) => navItem.to !== '/cloud/topology')
        // Cloud's landing page is what was discovered, not an activation funnel.
        .map((navItem) => (navItem.to === '/cloud/overview'
          ? { ...navItem, labelKey: 'discoveryDashboard' } : navItem))
        // Identities, not Users: most cloud principals are service accounts and roles.
        .map((navItem) => (navItem.labelKey === 'users'
          ? { ...navItem, to: '/cloud/identities', icon: 'key', labelKey: 'identities' }
          : navItem)),
      item('/cloud', 'inventory', 'database', 'inventory'),
      item('/cloud', 'knowledge-bases', 'book-open', 'knowledgeBases'),
    ],
    adoption: [],
    // Configuration findings, not access management — say what the page is. Alerts leave
    // Security: they are Notifications (the Atlas Alerts page pinned to cloud), their own item.
    security: groups.security
      .map((navItem) => (
        navItem.to === '/cloud/posture' ? { ...navItem, labelKey: 'posture' } : navItem
      )),
    notifications: item('/cloud', 'notifications', 'bell', 'alerts'),
    // No Settings page: it only linked to integrations, alert routing, incidents and inventory,
    // each reachable on its own.
    settings: null,
  };
};

export const localizeGeminiNavItem = (navItem, t) => ({
  ...navItem,
  label: navItem.label || t(`nav:items.${navItem.labelKey}`),
});
